/// <reference lib="dom" />
/** @jsx h */
import { h } from "preact";
import { useEffect } from "preact/hooks";
import * as d3 from "d3";
import { clamp } from "@utils/utils";
import type { ParamDict } from "@utils/params";
import { HzToNormalized, NormalizedToHz, OctaveSpan } from "@wbaudio/utils";
import type { ChartMode } from "../../core/mode.ts";
import { useChart, useLatest } from "../../core/useChart.ts";
import { BandLayer } from "../../layers/bands.ts";
import { MarkerLayer } from "../../layers/markers.ts";
import { type HandlePosition, HandlesLayer } from "../../layers/handles.ts";
import { FormatHz } from "../../utils/format.ts";
import { ChartPalette } from "../../utils/theme.ts";
import { CreateFrequencyChart, FrequencyDisplay } from "./frequencyChart.ts";

export interface WahWahCurveProps {
  params: ParamDict;
  baseFrequencyParam?: { min: number; max: number };
  excursionOctavesParam?: { min: number; max: number };
  onChange?: (key: string, value: unknown) => void;
}

const UnitRange = { min: 0, max: 1 };

function CreateWahWahChart(
  container: HTMLElement,
  mode: ChartMode,
  excursion: { min: number; max: number },
  onDrag: (id: string, position: HandlePosition) => void,
) {
  const band = BandLayer();
  const lines = MarkerLayer();
  const handles = mode === "editor"
    ? HandlesLayer({ y: d3.scaleLinear().domain([excursion.min, excursion.max]), onDrag })
    : null;
  const chart = CreateFrequencyChart(container, {
    mode,
    layers: [band, lines, ...(handles ? [handles] : [])],
  });
  return { chart, band, lines, handles, destroy: chart.destroy };
}

export function WahWahCurve(props: WahWahCurveProps) {
  const mode: ChartMode = props.onChange ? "editor" : "inline";
  const editor = mode === "editor";
  const latest = useLatest(props);
  const excursion = props.excursionOctavesParam ?? UnitRange;
  const { ref, chart } = useChart(
    (container) =>
      CreateWahWahChart(container, mode, excursion, (_, p) => {
        const { onChange, baseFrequencyParam = UnitRange } = latest.current;
        onChange?.(
          "baseFrequency",
          parseFloat(
            HzToNormalized(
              p.x,
              FrequencyDisplay.fMax,
              baseFrequencyParam.min,
              baseFrequencyParam.max,
            )
              .toFixed(4),
          ),
        );
        onChange?.("excursionOctaves", parseFloat(p.y.toFixed(1)));
      }),
    [mode, excursion.min, excursion.max],
  );

  const baseFrequency = props.params.baseFrequency.value as number;
  const octaves = props.params.excursionOctaves.value as number;
  const sweep = props.params.sweep.value as number;

  useEffect(() => {
    if (!chart) return;
    const { fMin, fMax } = FrequencyDisplay;
    const baseHz = NormalizedToHz(baseFrequency, fMin, fMax);
    const { lowHz, highHz } = OctaveSpan(baseHz, octaves, fMin, fMax);
    const sweepHz = clamp(lowHz + (highHz - lowHz) * sweep, fMin, fMax);
    const orange = ChartPalette.orange;
    chart.band.set([{ id: "band", x0: lowHz, x1: highHz, color: orange }]);
    chart.lines.set([
      { value: lowHz, color: orange, width: editor ? 1.5 : 1, opacity: editor ? 1 : 0.6 },
      { value: highHz, color: orange, opacity: editor ? 0.5 : 0.6 },
      {
        value: sweepHz,
        color: ChartPalette.white,
        opacity: editor ? 0.3 : 0.4,
        dashed: editor,
      },
    ]);
    chart.handles?.set([
      {
        id: "base",
        x: baseHz,
        y: octaves,
        radius: 5,
        color: orange,
        label: FormatHz(baseHz),
        sublabel: `${octaves.toFixed(1)}oct`,
      },
    ]);
    chart.chart.render();
  }, [chart, baseFrequency, octaves, sweep]);

  return <div ref={ref} />;
}
